import React from 'react';
import loc from 'owa-localize';
import { AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import type { AriaProperties } from 'owa-accessibility';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import type { ChevronProps } from 'owa-tree-node/lib/components/TreeNode';
import { HomeRegular, PersonRegular } from '@fluentui/react-icons';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import {
    myOutlookLabel,
    outlookTodayLabel,
    myProfileLabel,
} from './MyOutlookSection.locstring.json';
import { hasCachedSteadyState } from 'owa-personal-os-store/lib/utils/folderPaneCachedState';
import type { MyOutlookSectionProps } from './MyOutlookSection';

const MY_OUTLOOK_ROOT_NODE_ID = 'myOutlookRoot';

const ariaProps: AriaProperties = {
    role: AriaRoles.group,
    labelledBy: MY_OUTLOOK_ROOT_NODE_ID,
};

const noop = () => {};

const expandedChevronProps: ChevronProps = { isExpanded: true, onClick: noop };

/**
 * Lightweight placeholder rendered while the MyOutlookSection chunk loads.
 * Only shown when the per-account steady-state cache is warm, so it renders
 * the same "steady + mirror" rows the full component will show. This avoids
 * a blank slot in the folder pane during chunk download.
 *
 * Non-interactive: clicks are no-ops until the real component hydrates.
 */
const MyOutlookSectionPlaceholder: React.ForwardRefExoticComponent<
    MyOutlookSectionProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, MyOutlookSectionProps>(function MyOutlookSectionPlaceholder(
    props,
    ref
) {
    const { mailboxInfo } = props;

    // Only render if we have the cached steady state; otherwise show nothing
    // (matching the original behavior of LazyMyOutlookSection with no placeholder).
    if (!hasCachedSteadyState(mailboxInfo)) {
        return null;
    }

    const useNewSelectionUI = isGlobalFeatureEnabled('fp-newfoldersselection-ui');

    return (
        <div
            ref={ref}
            style={props.style}
            className={props.className}
            {...generateDomPropertiesForAria(ariaProps)}
        >
            <TreeNode
                id={MY_OUTLOOK_ROOT_NODE_ID}
                chevronProps={expandedChevronProps}
                displayName={loc(myOutlookLabel)}
                depth={0}
                isRootNode={true}
                isSelected={false}
                key="myOutlookRoot"
                ellipsesOnHover={true}
                shouldShowFocusBorder={true}
                mailboxInfo={mailboxInfo}
                useNewSelectionUI={useNewSelectionUI}
            />
            <TreeNode
                displayName={loc(outlookTodayLabel)}
                depth={0}
                isRootNode={false}
                isSelected={false}
                customIconComponent={HomeRegular}
                ellipsesOnHover={false}
                shouldShowFocusBorder={true}
                mailboxInfo={mailboxInfo}
                useNewSelectionUI={useNewSelectionUI}
            />
            <TreeNode
                displayName={loc(myProfileLabel)}
                depth={0}
                isRootNode={false}
                isSelected={false}
                customIconComponent={PersonRegular}
                ellipsesOnHover={false}
                shouldShowFocusBorder={true}
                mailboxInfo={mailboxInfo}
                useNewSelectionUI={useNewSelectionUI}
            />
        </div>
    );
});

export default MyOutlookSectionPlaceholder;
